import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { getWeekStart } from '../lib/utils';

function foodMatchesDislikes(recipe, dislikedNames) {
  return (recipe.main_foods || []).some(mf =>
    dislikedNames.some(dn =>
      dn.includes(mf.toLowerCase()) || mf.toLowerCase().includes(dn)
    )
  );
}

function isRecipeAllowed(recipe, userDiet, dislikedNames, cookingStyle) {
  const diet = (userDiet || '').toLowerCase();
  const excluded = recipe.excluded_from || [];
  if (
    (diet.includes('vegetarian') || diet.includes('vegan')) &&
    excluded.includes('vegetariana')
  ) return false;
  if (diet.includes('halal') && excluded.includes('halal')) return false;
  if (foodMatchesDislikes(recipe, dislikedNames)) return false;
  // cooking_style: null or 'ambos' = compatible with any style
  if (cookingStyle && recipe.cooking_style && recipe.cooking_style !== 'ambos') {
    if (recipe.cooking_style !== cookingStyle) return false;
  }
  return true;
}

function isFishRecipe(recipe, catMap) {
  return (recipe.main_foods || []).some(f => catMap[f.toLowerCase()] === 'pescados');
}

function isMeatRecipe(recipe, catMap) {
  return (recipe.main_foods || []).some(f => catMap[f.toLowerCase()] === 'carnes');
}

function isEggFood(foodName) {
  const fl = foodName.toLowerCase();
  return fl.includes('huevo') || fl.includes('yema') || fl.includes('clara');
}

// Sugerencia de contenido nutricional relevante para la persona elegida en el
// onboarding (p.ej. fitoestrógenos para menopausia) — nunca una exclusión ni
// un requisito, solo una preferencia de selección entre recetas ya permitidas.
const BOOST_WEIGHT = 3;

function buildBoostedFoodNames(foods, persona) {
  if (!persona) return new Set();
  return new Set(
    foods.filter(f => (f.persona_boost || []).includes(persona)).map(f => f.name.toLowerCase())
  );
}

function isRecipeBoosted(recipe, boostedNames) {
  return (recipe.main_foods || []).some(f => boostedNames.has(f.toLowerCase()));
}

export function usePlanner(userId, userProfile) {
  const [foods, setFoods] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [dislikes, setDislikes] = useState([]);
  const [dislikesLoaded, setDislikesLoaded] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    loadData();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadData() {
    setLoading(true);
    const [foodsRes, recipesRes, dislikesRes, planRes] = await Promise.all([
      supabase.from('foods').select('*').order('category').order('name'),
      supabase.from('recipes').select('*'),
      supabase.from('user_food_dislikes').select('food_id').eq('user_id', userId),
      supabase.from('meal_plans').select('*').eq('user_id', userId)
        .order('created_at', { ascending: false }).limit(1).maybeSingle(),
    ]);

    if (foodsRes.data) setFoods(foodsRes.data);
    if (recipesRes.data) setRecipes(recipesRes.data);

    const dislikeIds = dislikesRes.data?.map(d => d.food_id) || [];
    setDislikes(dislikeIds);
    setDislikesLoaded(true);

    if (planRes.data) {
      const weekStart = getWeekStart(new Date());
      if (planRes.data.week_start === weekStart) {
        setCurrentPlan(planRes.data.plan);
      }
    }

    setLoading(false);
  }

  async function saveDislikes(foodIds) {
    await supabase.from('user_food_dislikes').delete().eq('user_id', userId);
    if (foodIds.length > 0) {
      await supabase.from('user_food_dislikes').insert(
        foodIds.map(food_id => ({ user_id: userId, food_id }))
      );
    }
    setDislikes(foodIds);
  }

  const buildDislikedNames = useCallback((dislikeIds) => {
    return foods
      .filter(f => dislikeIds.includes(f.id))
      .map(f => f.name.toLowerCase());
  }, [foods]);

  function buildFoodCatMap() {
    const map = {};
    for (const f of foods) map[f.name.toLowerCase()] = f.category;
    return map;
  }

  // Pick one recipe from pool respecting ingredient constraints.
  // excludeIds: recipe IDs already picked in this slot (sibling recipes).
  function pickFromPool(pool, dayIngs, weeklyIngCount, excludeIds = [], boostedNames = new Set()) {
    const passesLimits = (r) => (r.main_foods || []).every(f => {
      const fl = f.toLowerCase();
      if (dayIngs.has(fl)) return false;
      return (weeklyIngCount[fl] || 0) < (isEggFood(fl) ? 7 : 5);
    });

    let candidates = pool.filter(r => !excludeIds.includes(r.id) && passesLimits(r));

    // Fallback 1: ignore same-day ings, keep weekly limit
    if (!candidates.length) {
      candidates = pool.filter(r =>
        !excludeIds.includes(r.id) &&
        (r.main_foods || []).every(f => (weeklyIngCount[f.toLowerCase()] || 0) < (isEggFood(f.toLowerCase()) ? 7 : 5))
      );
    }

    // Fallback 2: just exclude already-picked siblings
    if (!candidates.length) candidates = pool.filter(r => !excludeIds.includes(r.id));

    // Fallback 3: anything
    if (!candidates.length) candidates = pool;

    // Preferencia por persona: nudge, no requisito — las recetas no destacadas
    // siguen pudiendo salir elegidas, solo con menos probabilidad relativa.
    const weighted = candidates.flatMap(r =>
      Array(isRecipeBoosted(r, boostedNames) ? BOOST_WEIGHT : 1).fill(r)
    );

    return { ...weighted[Math.floor(Math.random() * weighted.length)] };
  }

  function trackIngredients(recipe, dayIngs, weeklyIngCount) {
    for (const f of (recipe.main_foods || [])) {
      const fl = f.toLowerCase();
      dayIngs.add(fl);
      weeklyIngCount[fl] = (weeklyIngCount[fl] || 0) + 1;
    }
  }

  // Returns a single recipe or array of 2, depending on meal type.
  // desayuno → single; comida/cena → [recipe1, recipe2]
  function generateMealSlot(pool, type, dayIngs, weeklyIngCount, catMap, preferFish, preferMeat, forceCount, boostedNames) {
    const count = forceCount !== undefined ? forceCount : (type === 'desayuno' ? 1 : 2);
    const picked = [];

    for (let slot = 0; slot < count; slot++) {
      let activePool = pool;

      // Apply fish/meat preference only to slot 0 of comida/cena
      if (slot === 0 && type !== 'desayuno' && (preferFish || preferMeat)) {
        const prefPool = pool.filter(r =>
          preferFish ? isFishRecipe(r, catMap) : isMeatRecipe(r, catMap)
        );
        if (prefPool.length) activePool = prefPool;
      }

      const recipe = pickFromPool(activePool, dayIngs, weeklyIngCount, picked.map(r => r.id), boostedNames);
      picked.push(recipe);
      trackIngredients(recipe, dayIngs, weeklyIngCount);
    }

    return count === 1 ? picked[0] : picked;
  }

  function generatePlan(overrideDislikes) {
    const dislikeIds = overrideDislikes !== undefined ? overrideDislikes : dislikes;
    const dislikedNames = buildDislikedNames(dislikeIds);
    const userDiet = userProfile?.diet || '';

    const cookingStyle = userProfile?.cooking_style || 'tradicional';
    const injectionDay = userProfile?.injection_day ?? null;
    const available = recipes.filter(r => isRecipeAllowed(r, userDiet, dislikedNames, cookingStyle));
    const boostedNames = buildBoostedFoodNames(foods, userProfile?.persona);

    const byType = {
      desayuno: available.filter(r => r.meal_type === 'desayuno'),
      comida:   available.filter(r => r.meal_type === 'comida'),
      cena:     available.filter(r => r.meal_type === 'cena'),
    };

    const catMap = buildFoodCatMap();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekStart = getWeekStart(today);

    const weeklyIngCount = {};
    const days = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dayIngs = new Set();

      // Fish/meat alternation: check first recipe of comida/cena from previous day
      let preferFish = false;
      let preferMeat = false;
      if (i > 0) {
        const prev = days[i - 1];
        const pc = Array.isArray(prev.meals.comida) ? prev.meals.comida[0] : prev.meals.comida;
        const pn = Array.isArray(prev.meals.cena)   ? prev.meals.cena[0]   : prev.meals.cena;
        if (pc && pn) {
          if (isFishRecipe(pc, catMap) && isFishRecipe(pn, catMap)) preferMeat = true;
          if (isMeatRecipe(pc, catMap) && isMeatRecipe(pn, catMap)) preferFish = true;
        }
      }

      const dow = date.getDay();
      const inInjectionWindow = injectionDay !== null && (
        dow === injectionDay || dow === (injectionDay + 1) % 7
      );

      const day = {
        date: date.toISOString().split('T')[0],
        fasting: false,
        fasting_window: null,
        injection_window: inInjectionWindow,
        injection_day_itself: inInjectionWindow && dow === injectionDay,
        meals: {},
      };

      for (const type of ['desayuno', 'comida', 'cena']) {
        const pool = byType[type];
        if (!pool.length) continue;
        const forceCount = (inInjectionWindow && type === 'comida') ? 1 : undefined;
        day.meals[type] = generateMealSlot(pool, type, dayIngs, weeklyIngCount, catMap, preferFish, preferMeat, forceCount, boostedNames);
      }

      days.push(day);
    }

    // Assign 2-3 indulgence days (postre or guarrada, never both same day)
    const dessertPool  = recipes.filter(r => r.meal_type === 'postre_especial' && isRecipeAllowed(r, userDiet, dislikedNames, cookingStyle));
    const guarradaPool = recipes.filter(r => r.meal_type === 'guarrada'        && isRecipeAllowed(r, userDiet, dislikedNames, cookingStyle));
    if (dessertPool.length || guarradaPool.length) {
      const numDays = Math.random() < 0.5 ? 2 : 3;
      const shuffled = [...Array(7).keys()].sort(() => Math.random() - 0.5);
      const usedIds = [];
      for (const i of shuffled.slice(0, numDays)) {
        const useGuarrada = guarradaPool.length && (Math.random() < 0.4 || !dessertPool.length);
        const pool = useGuarrada ? guarradaPool : dessertPool;
        const avail = pool.filter(r => !usedIds.includes(r.id));
        const pick  = avail.length ? avail : pool;
        const item  = { ...pick[Math.floor(Math.random() * pick.length)] };
        if (useGuarrada) {
          days[i].guarrada_day = true;
          days[i].meals.guarrada = item;
        } else {
          days[i].dessert_day = true;
          days[i].meals.postre = item;
        }
        usedIds.push(item.id);
      }
    }

    return { week_start: weekStart, days };
  }

  function buildShoppingList(plan) {
    const catMap = buildFoodCatMap();

    function findCategory(name) {
      const nl = name.toLowerCase();
      if (catMap[nl]) return catMap[nl];
      for (const [food, cat] of Object.entries(catMap)) {
        if (nl.includes(food) || (food.length > 3 && food.includes(nl))) return cat;
      }
      return 'otros';
    }

    function parseQty(raw) {
      const s = (raw || '').trim();
      const m = s.match(/^(\d+(?:[.,]\d+)?)\s*([a-zA-ZáéíóúüñÁÉÍÓÚÜÑ]*)/);
      if (m?.[1]) return { amount: parseFloat(m[1].replace(',', '.')), unit: (m[2] || 'u').toLowerCase() };
      return { amount: null, unit: s.toLowerCase() || 'u' };
    }

    const acc = {};
    for (const day of (plan.days || [])) {
      for (const slot of Object.values(day.meals || {})) {
        const items = Array.isArray(slot) ? slot : [slot];
        for (const recipe of items) {
          if (!recipe) continue;
          const mainFoods = (recipe.main_foods || []).map(f => f.toLowerCase());
          const allEntries = Object.entries(recipe.ingredients || {});
          const mainEntries = allEntries.filter(([n]) =>
            mainFoods.some(mf => n.toLowerCase().includes(mf) || mf.includes(n.toLowerCase()))
          );
          const entriesToUse = mainEntries.length > 0 ? mainEntries : allEntries.slice(0, 3);
          for (const [name, raw] of entriesToUse) {
            const { amount, unit } = parseQty(String(raw));
            const key = `${name.toLowerCase()}|||${unit}`;
            if (acc[key]) {
              if (amount !== null && acc[key].amount !== null) acc[key].amount += amount;
              else acc[key].count += 1;
            } else {
              acc[key] = { name, amount, unit, category: findCategory(name), count: 1 };
            }
          }
        }
      }
    }

    const grouped = {};
    for (const item of Object.values(acc)) {
      let display;
      if (item.amount !== null) {
        const n = Math.round(item.amount * 10) / 10;
        display = item.unit !== 'u' ? `${n} ${item.unit}` : `${n}`;
      } else if (item.count > 1) {
        display = `× ${item.count}`;
      } else {
        display = item.unit !== 'u' ? item.unit : '';
      }
      (grouped[item.category] = grouped[item.category] || []).push({ name: item.name, display });
    }
    return grouped;
  }

  async function savePlan(plan) {
    const { error } = await supabase.from('meal_plans').upsert(
      { user_id: userId, week_start: plan.week_start, plan },
      { onConflict: 'user_id,week_start' }
    );
    if (!error) setCurrentPlan(plan);
    return error;
  }

  // recipeIndex: 0 or 1 for comida/cena (which of the 2 recipes to swap)
  function shuffleRecipe(dayIndex, mealType, recipeIndex = 0) {
    if (!currentPlan) return;

    const day = currentPlan.days[dayIndex];
    const mealSlot = day.meals[mealType];
    const current = Array.isArray(mealSlot) ? mealSlot[recipeIndex] : mealSlot;
    if (!current) return;

    const dislikedNames = buildDislikedNames(dislikes);
    const userDiet = userProfile?.diet || '';
    let pool = recipes.filter(r => isRecipeAllowed(r, userDiet, dislikedNames, userProfile?.cooking_style));

    const dbMealType = mealType === 'postre' ? 'postre_especial' : mealType;
    const nativePool = pool.filter(r => r.meal_type === dbMealType);
    let extendedPool = [...nativePool];

    if (day.fasting && day.fasting_window) {
      const skipped = day.fasting_window.replace('skip_', '');
      extendedPool = [...extendedPool, ...pool.filter(r => r.meal_type === skipped)];
    }

    // Ingredients from other meals + sibling recipe in same slot
    const dayIngs = new Set([
      ...Object.entries(day.meals)
        .filter(([t]) => t !== mealType)
        .flatMap(([, m]) => {
          const items = Array.isArray(m) ? m : [m];
          return items.flatMap(r => (r?.main_foods || []).map(f => f.toLowerCase()));
        }),
      ...(Array.isArray(mealSlot)
        ? mealSlot.filter((_, i) => i !== recipeIndex).flatMap(r => (r?.main_foods || []).map(f => f.toLowerCase()))
        : []),
    ]);

    const siblingIds = Array.isArray(mealSlot)
      ? mealSlot.filter((_, i) => i !== recipeIndex).map(r => r.id)
      : [];

    const usedInWeek = currentPlan.days.flatMap(d => {
      const m = d.meals[mealType];
      if (Array.isArray(m)) return m.map(r => r.id);
      return m ? [m.id] : [];
    });

    const calLow  = current.calories_per_serving * 0.80;
    const calHigh = current.calories_per_serving * 1.20;
    const protLow  = current.protein_per_serving * 0.75;
    const protHigh = current.protein_per_serving * 1.25;

    const noSameDayIng = (r) => (r.main_foods || []).every(f => !dayIngs.has(f.toLowerCase()));
    const notSibling   = (r) => !siblingIds.includes(r.id);

    let candidates = extendedPool.filter(r =>
      r.id !== current.id && notSibling(r) && !usedInWeek.includes(r.id) && noSameDayIng(r) &&
      r.calories_per_serving >= calLow && r.calories_per_serving <= calHigh &&
      r.protein_per_serving  >= protLow && r.protein_per_serving  <= protHigh
    );

    if (!candidates.length) candidates = extendedPool.filter(r => r.id !== current.id && notSibling(r) && noSameDayIng(r));
    if (!candidates.length) candidates = extendedPool.filter(r => r.id !== current.id && notSibling(r));
    if (!candidates.length) return;

    const newRecipe = candidates[Math.floor(Math.random() * candidates.length)];

    const newMealSlot = Array.isArray(mealSlot)
      ? mealSlot.map((r, i) => i === recipeIndex ? { ...newRecipe } : r)
      : { ...newRecipe };

    const newPlan = {
      ...currentPlan,
      days: currentPlan.days.map((d, i) =>
        i !== dayIndex ? d : { ...d, meals: { ...d.meals, [mealType]: newMealSlot } }
      ),
    };

    setCurrentPlan(newPlan);
    savePlan(newPlan);
  }

  function toggleFasting(dayIndex, window) {
    if (!currentPlan) return;

    const day = currentPlan.days[dayIndex];
    const turning_off = day.fasting && day.fasting_window === window;

    const dislikedNames = buildDislikedNames(dislikes);
    const userDiet = userProfile?.diet || '';
    const pool = recipes.filter(r => isRecipeAllowed(r, userDiet, dislikedNames, userProfile?.cooking_style));

    const newPlan = {
      ...currentPlan,
      days: currentPlan.days.map((d, i) => {
        if (i !== dayIndex) return d;

        if (turning_off) {
          const skipped = d.fasting_window?.replace('skip_', '');
          const newDay = { ...d, fasting: false, fasting_window: null };

          if (skipped) {
            const options = pool.filter(r => r.meal_type === skipped);
            if (options.length) {
              const count = skipped !== 'desayuno' ? 2 : 1;
              const picked = [];
              const usedIds = new Set();
              for (let s = 0; s < count; s++) {
                const avail = options.filter(r => !usedIds.has(r.id));
                const r = (avail.length ? avail : options)[Math.floor(Math.random() * (avail.length || options.length))];
                picked.push({ ...r });
                usedIds.add(r.id);
              }
              newDay.meals = {
                ...d.meals,
                [skipped]: count === 1 ? picked[0] : picked,
              };
            }
          }
          return newDay;
        }

        const skipped = window.replace('skip_', '');
        const meals = { ...d.meals };
        delete meals[skipped];
        return { ...d, fasting: true, fasting_window: window, meals };
      }),
    };

    setCurrentPlan(newPlan);
    savePlan(newPlan);
  }

  return {
    foods,
    recipes,
    dislikes,
    dislikesLoaded,
    currentPlan,
    loading,
    saveDislikes,
    generatePlan,
    savePlan,
    shuffleRecipe,
    toggleFasting,
    buildShoppingList,
  };
}
